import {
  Button,
  CameraIcon,
  EmptyState,
  IconButton,
  List,
  ListRow,
  SearchInput,
  SectionLabel,
} from '@gramas/ui'
import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useRef, useState } from 'react'
import { browseFoods, FOOD_CATEGORIES, searchFoodsEverywhere } from '../api/foods.js'
import { db, type LocalFood } from '../db/dexie.js'
import { favouriteFoods, foodDisplayName, toggleFoodFavourite } from '../db/repo.js'
import { ShelfMotif } from '../glitime/motifs.js'
import { PersonaSpinner } from '../glitime/PersonaSpinner.js'
import { sassLine } from '../sass/engine.js'
import { useSassOnce } from '../sass/useSassOnce.js'
import { SwipeableRow } from '../shell/SwipeableRow.js'
import { CategoryGrid } from './CategoryGrid.js'
import { LogFoodSheet } from './sheets/LogFoodSheet.js'
import { ManualFoodSheet } from './sheets/ManualFoodSheet.js'
import { PhotoSearchSheet } from './sheets/PhotoSearchSheet.js'

/**
 * Finding a food that is not in front of a scanner.
 *
 * Three ways in, because the products people eat divide neatly into three
 * kinds: typing finds packaged goods, the grid finds what a search box cannot
 * (an apple has no barcode, no brand and nothing to type that the user can be
 * sure the catalogue agrees with), and the camera finds whatever is on the
 * plate. Local results render as the user types and the server's are merged in
 * when they arrive, so a food eaten before never waits on a request.
 *
 * With nothing typed it opens on Mėgstamiausi — starred foods first, then
 * whatever the diary shows gets eaten regularly — and any row in the list,
 * search results included, is starred by swiping it to the right.
 */
export function FoodList() {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<LocalFood[] | null>(null)
  const [category, setCategory] = useState<string | null>(null)
  const [browsed, setBrowsed] = useState<LocalFood[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [selected, setSelected] = useState<LocalFood | null>(null)
  const [manualOpen, setManualOpen] = useState(false)
  const [photoOpen, setPhotoOpen] = useState(false)
  const [editing, setEditing] = useState<LocalFood | null>(null)
  const photoInput = useRef<HTMLInputElement | null>(null)
  const [photo, setPhoto] = useState<File | null>(null)

  // What opens the screen when nobody has typed anything: the foods this person
  // starred, then the ones the diary shows they keep eating. Both come from the
  // user's own data rather than from whatever happened to land in the cache.
  const favourites = useLiveQuery(() => favouriteFoods(40), [], [])
  // Dexie leaves a null index key out entirely, so ordering by foodId visits
  // only the food favourites — recipes and builds are starred elsewhere.
  const starred = useLiveQuery(
    async () => new Set((await db.favourites.orderBy('foodId').keys()).map(String)),
    [],
    new Set<string>(),
  )

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults(null)
      return
    }
    let cancelled = false
    setSearching(true)
    const handle = window.setTimeout(async () => {
      const merged = await searchFoodsEverywhere(query, 40, (local) => {
        if (!cancelled) setResults(local)
      })
      if (!cancelled) {
        setResults(merged)
        setSearching(false)
      }
    }, 200)
    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [query])

  useEffect(() => {
    if (category == null) {
      setBrowsed(null)
      return
    }
    let cancelled = false
    setSearching(true)
    void browseFoods(category)
      .catch(() => [])
      .then((foods) => {
        if (cancelled) return
        setBrowsed(foods)
        setSearching(false)
      })
    return () => {
      cancelled = true
    }
  }, [category])

  const shown = results ?? browsed ?? favourites ?? []
  const heading = results
    ? 'Paieškos rezultatai'
    : (FOOD_CATEGORIES.find((c) => c.key === category)?.label ?? 'Mėgstamiausi')

  const isEmpty = !searching && shown.length === 0
  const emptySurface = results != null || category != null ? 'search' : 'favourites'
  const emptyTitle = useSassOnce(isEmpty ? emptySurface : null, () => {
    const fallback = results
      ? 'Nieko nerasta'
      : category != null
        ? 'Produktų sąrašas tuščias'
        : 'Mėgstamiausių dar nėra'
    return sassLine('empty_list', { surface: emptySurface }) ?? fallback
  })

  return (
    <div className="flex h-full flex-col">
      <div data-tour="foods-search" className="flex shrink-0 items-center gap-2 px-3 py-2">
        <SearchInput
          value={query}
          onValueChange={setQuery}
          label="Ieškoti produkto"
          placeholder="Ieškoti produkto…"
        />
        <IconButton label="Ieškoti pagal nuotrauką" onClick={() => photoInput.current?.click()}>
          <CameraIcon />
        </IconButton>
        <Button variant="primary" size="sm" onClick={() => setManualOpen(true)}>
          Naujas
        </Button>
      </div>

      {/* Photographs come from the roll as readily as from the camera: a plate
          is easier to shoot first and log afterwards. */}
      <input
        ref={photoInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (!file) return
          setPhoto(file)
          setPhotoOpen(true)
        }}
      />

      {query.trim().length < 2 && <CategoryGrid value={category} onChange={setCategory} />}

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <SectionLabel>{heading}</SectionLabel>

        {searching && shown.length === 0 && (
          <div className="flex justify-center py-8">
            <PersonaSpinner surface="search" />
          </div>
        )}

        {!searching && shown.length === 0 && (
          <EmptyState
            motif={<ShelfMotif />}
            title={emptyTitle ?? ''}
            hint={
              results || category != null
                ? 'Nuskaitykite etiketę, pasirinkite grupę arba nufotografuokite maistą.'
                : 'Braukite produktą į dešinę — jis liks čia. Dažnai valgomi atsiras patys.'
            }
          />
        )}

        <List className="border-y border-line bg-surface">
          {shown.map((food, i) => (
            <SwipeableRow
              key={food.id}
              favourite={starred.has(food.id)}
              onFavourite={() => void toggleFoodFavourite(food)}
            >
              <ListRow
                last={i === shown.length - 1}
                title={foodDisplayName(food)}
                subtitle={`B ${Math.round(food.protein)} · A ${Math.round(food.carbs)} · R ${Math.round(food.fat)}`}
                value={`${Math.round(food.kcal)}`}
                meta={`kcal/100 ${food.unit}`}
                onActivate={() => setSelected(food)}
              />
            </SwipeableRow>
          ))}
        </List>
        <div className="h-6" />
      </div>

      <LogFoodSheet
        open={selected != null && editing == null}
        food={selected}
        onEdit={setEditing}
        onOpenChange={(open) => !open && setSelected(null)}
      />
      <ManualFoodSheet open={manualOpen} onOpenChange={setManualOpen} onSaved={setSelected} />
      <ManualFoodSheet
        open={editing != null}
        food={editing}
        onOpenChange={(open) => !open && setEditing(null)}
      />
      <PhotoSearchSheet
        open={photoOpen}
        photo={photo}
        onOpenChange={(open) => {
          setPhotoOpen(open)
          if (!open) setPhoto(null)
        }}
        onPick={(food) => {
          setPhotoOpen(false)
          setPhoto(null)
          setSelected(food)
        }}
      />
    </div>
  )
}
